import { ref, computed, Ref } from "@vue/composition-api";
import { NumericRange } from "@web-components/types/NumericRange";
import { UseStringValidationResponse } from "@web-components/types/UseStringValidationResponse";
import {
  RequiredAttr,
  requiredValidator
} from "./validators/requiredValidator";
import { CustomAttr, customValidator } from "./validators/customValidator";
import { NumericAttr, numericValidator } from "./validators/numericValidator";
import { RangeAttr, rangeValidator } from "./validators/rangeValidator";
import { RegExAttr, regExValidator } from "./validators/regexValidator";
import {
  MinLengthAttr,
  minLengthValidator
} from "./validators/minLengthValidator";
import { ValidationMessage } from "@web-components/types/ValidationMessage";

interface StringValidationArgs {
  inputRef: Ref<string | number>;
  required?: boolean;
  range?: NumericRange;
  numeric?: boolean;
  custom?: (value: string) => boolean;
  minLength?: number;
  regex?: RegExp;
}

export const useStringValidation = (
  args: StringValidationArgs
): UseStringValidationResponse => {
  const { inputRef, required, range, numeric, custom, minLength, regex } = args;

  const inputValueDirty: Ref<boolean> = ref(false);

  let requiredAttr: RequiredAttr;
  if (required) {
    requiredAttr = requiredValidator(inputRef, inputValueDirty);
  } else {
    requiredAttr = { required: ref(true), requiredError: ref(false) };
  }

  let numericAttr: NumericAttr;
  if (numeric) {
    numericAttr = numericValidator(inputRef, inputValueDirty);
  } else {
    numericAttr = { numeric: ref(true), numericError: ref(false) };
  }

  let rangeAttr: RangeAttr;
  if (range) {
    rangeAttr = rangeValidator(inputRef, inputValueDirty, range);
  } else {
    rangeAttr = { between: ref(true), betweenError: ref(false) };
  }

  let customAttr: CustomAttr;
  if (custom) {
    customAttr = customValidator(inputRef, inputValueDirty, custom);
  } else {
    customAttr = { custom: ref(true), customError: ref(false) };
  }

  let regexAttr: RegExAttr;
  if (regex) {
    regexAttr = regExValidator(inputRef, inputValueDirty, regex);
  } else {
    regexAttr = { regex: ref(true), regexError: ref(false) };
  }

  let minLengthAttr: MinLengthAttr;
  if (minLength) {
    minLengthAttr = minLengthValidator(inputRef, inputValueDirty, minLength);
  } else {
    minLengthAttr = { minLength: ref(true), minLengthError: ref(false) };
  }

  const valid = computed(() => {
    return (
      requiredAttr.required.value &&
      numericAttr.numeric.value &&
      rangeAttr.between.value &&
      customAttr.custom.value &&
      regexAttr.regex.value &&
      minLengthAttr.minLength.value
    );
  });

  return {
    dirty: inputValueDirty,
    valid,
    setDirty: value => (inputValueDirty.value = value),
    ...requiredAttr,
    ...customAttr,
    ...regexAttr,
    ...numericAttr,
    ...rangeAttr,
    ...minLengthAttr
  };
};

export const formatValidationResponse = (
  response: UseStringValidationResponse
): ValidationMessage => {
  return {
    valid: response.valid.value,
    numeric: response.numeric ? response.numeric.value : true,
    required: response.required ? response.required.value : true,
    between: response.between ? response.between.value : true,
    regex: response.regex ? response.regex.value : true,
    custom: response.custom ? response.custom.value : true,
    minLength: response.minLength ? response.minLength.value : true
  };
};
